import { DownOutlined, FileOutlined, UpOutlined } from "@ant-design/icons"
import { Flex, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  type ChangeRow,
  changeRowsFromChanges,
  changeVerbForAction,
  displayChangeValue,
  hasChangeDetails
} from "./project-log-changes.ts"

const { Text } = Typography

const MAX_VISIBLE_FIELDS = 4

/** "Attachments · 2 added, 3 removed" — omits whichever side is empty. */
function listDiffSummary(row: ChangeRow): string {
  const parts: string[] = []
  if (row.listDiff!.added.length)
    parts.push(`${row.listDiff!.added.length} added`)
  if (row.listDiff!.removed.length)
    parts.push(`${row.listDiff!.removed.length} removed`)
  return `${row.field} · ${parts.join(", ")}`
}

/** One item changed hands — "Attachments · quick-test.png [Removed]", no expand needed. */
function InlineListDiffLine({ row }: { row: ChangeRow }) {
  const { added, removed } = row.listDiff!
  const [name, isAdded] = added.length ? [added[0], true] : [removed[0], false]

  return (
    <Text className={"text-sm"}>
      <Text strong className={"text-sm"}>
        {row.field}
      </Text>
      {" · "}
      <Text className={"text-sm"}>{name}</Text>{" "}
      <Tag color={isAdded ? "success" : "error"} className={"m-0"}>
        {isAdded ? "+ Added" : "− Removed"}
      </Tag>
    </Text>
  )
}

function FileChip({ name }: { name: string }) {
  return (
    <Tag className={"m-0 inline-flex items-center gap-1 bg-white"}>
      <FileOutlined className={"text-gray-400"} />
      {name}
    </Tag>
  )
}

/** Explicit Added/Removed groups — replaces a confusing green-arrow reading for a file list. */
function ListDiffBlock({ row }: { row: ChangeRow }) {
  const { added, removed } = row.listDiff!
  return (
    <Flex vertical gap={8}>
      {!!added.length && (
        <div
          className={"rounded-md border border-solid p-2"}
          style={{ borderColor: "#B7EB8F", background: "#F6FFED" }}
        >
          <Text
            strong
            className={"text-xs"}
            style={{ color: "var(--ant-color-success)" }}
          >
            + Added ({added.length})
          </Text>
          <Flex wrap gap={4} className={"mt-1"}>
            {added.map((name) => (
              <FileChip key={name} name={name} />
            ))}
          </Flex>
        </div>
      )}
      {!!removed.length && (
        <div
          className={"rounded-md border border-solid p-2"}
          style={{ borderColor: "#FFCCC7", background: "#FFF1F0" }}
        >
          <Text
            strong
            className={"text-xs"}
            style={{ color: "var(--ant-color-error)" }}
          >
            − Removed ({removed.length})
          </Text>
          <Flex wrap gap={4} className={"mt-1"}>
            {removed.map((name) => (
              <FileChip key={name} name={name} />
            ))}
          </Flex>
        </div>
      )}
    </Flex>
  )
}

/** "Status · Estimate → Contract" — one line, no arrow when there was no previous value. */
function InlineChangeLine({ row }: { row: ChangeRow }) {
  const isNewValue = row.from == null || row.from === ""
  return (
    <Text className={"text-sm"}>
      <Text strong className={"text-sm"}>
        {row.field}
      </Text>
      {" · "}
      {isNewValue ? (
        displayChangeValue(row.to)
      ) : (
        <>
          <Text type={"secondary"} className={"text-sm"}>
            {displayChangeValue(row.from)}
          </Text>
          <Text type={"secondary"}> → </Text>
          <Text
            strong
            className={"text-sm"}
            style={{ color: "var(--ant-color-success)" }}
          >
            {displayChangeValue(row.to)}
          </Text>
        </>
      )}
    </Text>
  )
}

/** Field name on its own line, value line beneath — used inside the expanded stack. */
function StackedChangeLine({ row }: { row: ChangeRow }) {
  if (row.listDiff) {
    return (
      <div>
        <Text strong className={"block text-xs"}>
          {row.field}
        </Text>
        <div className={"mt-1"}>
          <ListDiffBlock row={row} />
        </div>
      </div>
    )
  }

  const isNewValue = row.from == null || row.from === ""
  return (
    <div>
      <Text strong className={"block text-xs"}>
        {row.field}
      </Text>
      {isNewValue ? (
        <Text className={"text-xs"}>{displayChangeValue(row.to)}</Text>
      ) : (
        <Text className={"text-xs"}>
          <Text type={"secondary"} className={"text-xs"}>
            {displayChangeValue(row.from)}
          </Text>
          <Text type={"secondary"}> → </Text>
          <Text
            strong
            className={"text-xs"}
            style={{ color: "var(--ant-color-success)" }}
          >
            {displayChangeValue(row.to)}
          </Text>
        </Text>
      )}
    </div>
  )
}

interface ProjectLogDetailsCellProps {
  entryKey: string
  action: string
  changes: unknown
  expanded: boolean
  showAll: boolean
  onToggleExpand: () => void
  onToggleShowAll: () => void
}

/**
 * Stays inside the Details cell at every field count — a single scalar change
 * reads inline with no expand affordance (nothing more to reveal); a single
 * file-list change still collapses behind a count summary since the actual
 * filenames are real additional detail; 2+ changes collapse to a "N fields
 * changed" toggle; a long change set caps what's shown until "+K more" is
 * clicked, so one huge operation can't blow out the row height by default.
 */
export function ProjectLogDetailsCell({
  entryKey,
  action,
  changes,
  expanded,
  showAll,
  onToggleExpand,
  onToggleShowAll
}: ProjectLogDetailsCellProps) {
  const { t } = useTranslation()
  const rows = changeRowsFromChanges(changes, {
    lines: t("projectLogs.fields.lines")
  })

  if (!rows) {
    if (typeof changes === "string" && changes.trim()) {
      return <Text className={"text-sm"}>{changes}</Text>
    }
    return hasChangeDetails(changes) ? (
      <Text type={"secondary"} className={"text-sm"}>
        Details unavailable
      </Text>
    ) : (
      <Text type={"secondary"}>—</Text>
    )
  }

  const toggleButtonClass =
    "inline-flex items-center gap-1 border-0 bg-transparent p-0 text-sm text-gray-600 hover:text-gray-900"

  if (rows.length === 1 && !rows[0].listDiff)
    return <InlineChangeLine row={rows[0]} />

  if (
    rows.length === 1 &&
    rows[0].listDiff &&
    rows[0].listDiff.added.length + rows[0].listDiff.removed.length === 1
  )
    return <InlineListDiffLine row={rows[0]} />

  const summary =
    rows.length === 1
      ? listDiffSummary(rows[0])
      : `${rows.length} field${rows.length === 1 ? "" : "s"} ${changeVerbForAction(action)}`

  if (!expanded) {
    return (
      <button
        type={"button"}
        className={toggleButtonClass}
        onClick={onToggleExpand}
        data-testid={`project-log-details-toggle-${entryKey}`}
      >
        <span>{summary}</span>
        <DownOutlined className={"text-[10px]"} />
      </button>
    )
  }

  const visibleRows = showAll ? rows : rows.slice(0, MAX_VISIBLE_FIELDS)
  const remaining = rows.length - visibleRows.length

  return (
    <Flex vertical gap={4} className={"max-w-[520px]"}>
      <button
        type={"button"}
        className={toggleButtonClass}
        onClick={onToggleExpand}
        data-testid={`project-log-details-toggle-${entryKey}`}
      >
        <span>{summary}</span>
        <UpOutlined className={"text-[10px]"} />
      </button>
      <Flex vertical gap={8}>
        {visibleRows.map((row) => (
          <StackedChangeLine key={row.key} row={row} />
        ))}
      </Flex>
      {remaining > 0 && (
        <button
          type={"button"}
          className={
            "w-fit border-0 bg-transparent p-0 text-xs text-blue-600 hover:underline"
          }
          onClick={onToggleShowAll}
          data-testid={`project-log-details-showmore-${entryKey}`}
        >
          +{remaining} more change{remaining === 1 ? "" : "s"}
        </button>
      )}
    </Flex>
  )
}
